import { type DatasourceDataResponse } from '@atlaskit/linking-types';
import { CloudId } from '@databases/types/confluence';
import { getThrottledBatchCallback } from '@databases/utils/utils';
import { groupBy } from 'lodash-es';

import { type DatasourceType as ConnectionType } from '@canvas/atlassian-service/datasource';

import { getDatasourceResponseByType } from './datasource';

export const resolveConnectionContent = getThrottledBatchCallback<
  [
    ari: string,
    columns: string[],
    type: ConnectionType,
    cloudId: CloudId,
    connectionId: string
  ],
  {
    connectionId: string;
    datasourceResponse: DatasourceDataResponse;
  }[]
>(async (args) => {
  return [await resolveContentByDatasource(args)];
}, 300);

async function resolveContentByDatasource(
  args: Array<
    [
      ari: string,
      columns: string[],
      type: ConnectionType,
      cloudId: CloudId,
      connectionId: string
    ]
  >
): Promise<
  {
    connectionId: string;
    datasourceResponse: DatasourceDataResponse;
  }[]
> {
  const uniqueArgs = Array.from(new Map(args.map((arg) => [arg[0], arg])).values());
  const groupedArgs = groupBy(
    uniqueArgs,
    ([, , type, cloudId, connectionId]) => `${cloudId}::${type}::${connectionId}`
  );

  const groupedData: {
    [key: string]: { aris: string[]; columns: string[] };
  } = {};
  Object.entries(groupedArgs).forEach(([key, group]) => {
    groupedData[key] = {
      aris: group.map(([ari]) => ari).flat(),
      columns: Array.from(new Set(group.map(([, columns]) => columns).flat())),
    };
  });

  return await Promise.all(
    Object.entries(groupedData).map(async ([groupKey, data]) => {
      const [cloudId, type, connectionId] = groupKey.split('::');
      return {
        connectionId,
        datasourceResponse: await getDatasourceResponseByType(
          type as ConnectionType,
          data.columns,
          {
            cloudId,
            contentARIs: data.aris,
          }
        ),
      };
    })
  );
}
